import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy,
  afterRenderEffect,
  ElementRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter } from 'rxjs';
import { Installation } from '../../Services/EntiteProjet';
import { EtatService } from '../../Services/etat';
import { statusLabel, statusClass } from '../../Services/format';
import {
  QM_PRODUCTION,
  QM_PERTE,
  QM_PUISSANCE_CRETE,
  QuickMetricItem,
  QuickMetricsGridComponent,
  formatProductionInstantanee,
  formatPuissanceCrete,
  tooltipProductionInstantanee,
} from '../quick-metrics-grid/quick-metrics-grid';

@Component({
  selector: 'app-silo-parc',
  imports: [CommonModule, RouterLink, QuickMetricsGridComponent],
  templateUrl: './silo-parc.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './silo-parc.css',
})
export class SiloParc {
  private etat = inject(EtatService);
  private router = inject(Router);
  private readonly champRecherche = viewChild<ElementRef<HTMLInputElement>>('champRecherche');
  // Attendre que le champ conditionnel soit rendu avant de lui donner le focus.
  private readonly focusRecherche = afterRenderEffect(() => {
    this.champRecherche()?.nativeElement.focus();
  });

  recherche = signal('');
  filtreVisible = signal(false);
  listeVisibleMobile = signal(false);
  filtresActifs = signal<Set<number>>(new Set());
  hoveredSegment = signal<{ label: string; count: number } | null>(null);

  vueCarte = signal(false);
  total = computed(() => this.etat.installations().length);

  readonly statusLabel = statusLabel;
  readonly statusClass = statusClass;

  etatservice = EtatService;

  parNiveau = computed(() => {
    const counts: Record<number, number> = {};
    for (const inst of this.installationsFiltrees()) {
      const n = inst.etat?.niveau;
      if (n != null) counts[n] = (counts[n] ?? 0) + 1;
    }
    return counts;
  });

  installationCourante = this.etat.installationCourante;

  liste_installations = computed(() => {
    let liste = this.etat.installations;

    // Recalcule l'état de chaque installation avant tri/filtrage.
    liste().forEach((inst) => {
      if (inst.etat) inst.etat.niveau = EtatService.calculerNiveau(inst.etat);
    });

    return liste();
  });

  installationsFiltrees = computed(() => {
    const q = this.recherche().trim().toLowerCase();
    const filtres = this.filtresActifs();
    let liste = this.liste_installations();
    if (q) liste = liste.filter((i) => i.nom.toLowerCase().includes(q) || String(i.id) === q);
    if (filtres.size > 0) liste = liste.filter((i) => filtres.has(i.etat?.niveau ?? -1));
    const rang = (niveau: number | undefined): number => {
      return EtatService.rangNiveau(niveau ?? Infinity);
    };
    return [...liste].sort((a, b) => rang(a.etat?.niveau) - rang(b.etat?.niveau));
  });

  donutStyle = computed(() => {
    const t = this.installationsFiltrees().length;
    if (t === 0) return '';
    const n = this.parNiveau();
    const pct = (niveau: number) => ((n[niveau] ?? 0) / t) * 100;
    const c1 = pct(EtatService.NIVEAU_ARRET);
    const c2 = c1 + pct(EtatService.NIVEAU_HORSLIGNE);
    const c3 = c2 + pct(EtatService.NIVEAU_PERTE);
    const c4 = c3 + pct(EtatService.NIVEAU_SANS_PERTE);
    const c5 = c4 + pct(EtatService.NIVEAU_NORMAL);
    return [
      'radial-gradient(circle at center, #ffffff 0 39%, transparent 40%)',
      `conic-gradient(var(--red) 0 ${c1.toFixed(1)}%, #1e1e1e ${c1.toFixed(1)}% ${c2.toFixed(1)}%, var(--amber) ${c2.toFixed(1)}% ${c3.toFixed(1)}%, var(--violet) ${c3.toFixed(1)}% ${c4.toFixed(1)}%, var(--green) ${c4.toFixed(1)}% ${c5.toFixed(1)}%)`,
    ].join(', ');
  });

  donutSegments = computed(() => {
    const n = this.parNiveau();
    const t = this.installationsFiltrees().length;
    if (t === 0) return [];
    const defs = [
      { niveau: EtatService.NIVEAU_HORSLIGNE, label: 'Hors ligne' },
      { niveau: EtatService.NIVEAU_ARRET, label: "A l'arrêt" },
      { niveau: EtatService.NIVEAU_PERTE, label: 'Avec perte' },
      { niveau: EtatService.NIVEAU_SANS_PERTE, label: 'Sans perte' },
      { niveau: EtatService.NIVEAU_NORMAL, label: 'Normal' },
    ];
    let cumul = 0;
    return defs
      .sort((a, b) => EtatService.rangNiveau(a.niveau) - EtatService.rangNiveau(b.niveau))
      .map((s) => {
        const count = n[s.niveau] ?? 0;
        const start = cumul;
        cumul += count / t;
        return { ...s, count, path: this.segmentPath(start, cumul) };
      })
      .filter((s) => s.count > 0);
  });

  private currentUrl = signal(this.router.url);

  private contexteUrl = computed(() => {
    const url = this.currentUrl().split(/[?#]/)[0];
    if (url === '/materiel' || (url.startsWith('/installation/') && url.endsWith('/materiel')))
      return 'materiel';
    if (url === '/graphiques' || (url.startsWith('/installation/') && url.endsWith('/graphiques')))
      return 'graphiques';
    if (url === '/tickets' || (url.startsWith('/installation/') && url.endsWith('/tickets')))
      return 'tickets';
    return null;
  });

  lienInstallation(id: number): (string | number)[] {
    const ctx = this.contexteUrl();
    if (ctx) return ['/installation', id, ctx];
    return ['/installation', id];
  }

  constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((e) => {
        this.currentUrl.set(e.urlAfterRedirects);
        this.listeVisibleMobile.set(false);
      });
  }

  private segmentPath(start: number, end: number): string {
    const cx = 50,
      cy = 50,
      outerR = 48,
      innerR = 22;
    const toRad = (f: number) => f * 2 * Math.PI - Math.PI / 2;
    const sa = toRad(start),
      ea = toRad(end);
    const largeArc = end - start > 0.5 ? 1 : 0;
    const xAt = (r: number, a: number) => (cx + r * Math.cos(a)).toFixed(2);
    const yAt = (r: number, a: number) => (cy + r * Math.sin(a)).toFixed(2);
    return [
      `M ${xAt(outerR, sa)} ${yAt(outerR, sa)}`,
      `A ${outerR} ${outerR} 0 ${largeArc} 1 ${xAt(outerR, ea)} ${yAt(outerR, ea)}`,
      `L ${xAt(innerR, ea)} ${yAt(innerR, ea)}`,
      `A ${innerR} ${innerR} 0 ${largeArc} 0 ${xAt(innerR, sa)} ${yAt(innerR, sa)}`,
      'Z',
    ].join(' ');
  }

  quickMetricsForInstallation(installation: Installation): QuickMetricItem[] {
    const pc = installation.pc_affichee;
    const production = installation.productionInstantannee?.value;
    const dateProduction = installation.productionInstantannee?.date;
    return [
      { ...QM_PUISSANCE_CRETE, value: pc != null ? formatPuissanceCrete(pc) : '—' },
      {
        ...QM_PRODUCTION,
        value: formatProductionInstantanee(production),
        tooltip: tooltipProductionInstantanee(dateProduction, production),
      },
      // { ...QM_PERTE,           value: '200€' },
    ];
  }

  filtreEtatListeInstallations(niveau: number): void {
    this.filtresActifs.update((set) => {
      const next = new Set(set);
      if (next.has(niveau)) next.delete(niveau);
      else next.add(niveau);
      return next;
    });
  }
  toggleVue() {
    this.vueCarte.update((v) => !v);
  }
}
